/**
 * Microsoft-365-Verknüpfung (Outlook-Integration).
 *
 * Zeigt Status (verknüpft/nicht verknüpft), Verknüpfen-Button (→ OAuth-Redirect),
 * Trennen-Button und einen Testversand-Knopf.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { MicrosoftOutlookLogo, PlugsConnected, PaperPlaneTilt, X, CheckCircle } from "@phosphor-icons/react";

export default function MicrosoftIntegrationPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () => {
    api.get("/microsoft/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ linked: false }));
  };
  useEffect(() => { load(); }, []);

  // OAuth-Callback-Feedback via URL-Parameter (?ms_linked=1 oder ?ms_error=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("ms_linked") === "1") {
      toast.success("Microsoft-Konto verknüpft. Mails gehen nun über Outlook.");
      load();
      const clean = new URL(window.location.href);
      clean.searchParams.delete("ms_linked");
      window.history.replaceState({}, "", clean.toString());
    } else if (params.get("ms_error")) {
      toast.error("Microsoft-Verknüpfung fehlgeschlagen: " + params.get("ms_error"));
      const clean = new URL(window.location.href);
      clean.searchParams.delete("ms_error");
      window.history.replaceState({}, "", clean.toString());
    }
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/microsoft/login");
      // Full page redirect zu Microsoft-Consent
      window.location.href = data.url;
    } catch (e) {
      toast.error("Anmelde-URL konnte nicht geladen werden.");
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!await window._domivusConfirm("Microsoft-Verknüpfung wirklich trennen? E-Mails gehen dann wieder über Resend.")) return;
    try {
      await api.post("/microsoft/disconnect");
      toast.success("Verknüpfung entfernt.");
      load();
    } catch {
      toast.error("Trennen fehlgeschlagen.");
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) {
      toast.error("Bitte Empfänger-Adresse angeben.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/microsoft/test", { to: testEmail });
      toast.success(`Test-Mail über Outlook gesendet: ${data.provider || "microsoft"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Testversand fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  if (!status) {
    return <div className="text-sm text-slate-500 italic">Lade…</div>;
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="ms-integration-panel">
      <div className="bg-slate-50 border-b border-slate-200 p-4 flex items-center gap-3">
        <MicrosoftOutlookLogo size={28} className="text-blue-700" />
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">Microsoft 365 / Outlook</h3>
          <p className="text-xs text-slate-500">
            Versende E-Mails direkt aus Ihrem Outlook &mdash; sie erscheinen dann in &bdquo;Gesendete Elemente&ldquo;.
          </p>
        </div>
        {status.linked ? (
          <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> Verknüpft
          </span>
        ) : (
          <span className="text-[10px] px-2 py-1 rounded-full bg-slate-200 text-slate-600 font-semibold">
            Nicht verknüpft
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {status.linked ? (
          <>
            <div className="text-sm text-slate-700">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Verknüpftes Konto</div>
              <div><strong>{status.email}</strong>{status.display_name ? ` — ${status.display_name}` : ""}</div>
              {status.linked_at && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Verbunden seit {new Date(status.linked_at).toLocaleDateString("de-DE")}
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Testversand</div>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="empfänger@beispiel.de"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1 text-sm"
                  data-testid="ms-test-email"
                />
                <Button size="sm" onClick={handleTestSend} disabled={testing || !testEmail}
                        className="bg-blue-600 hover:bg-blue-700" data-testid="ms-test-send">
                  <PaperPlaneTilt size={14} className="mr-1.5" />
                  {testing ? "Sende…" : "Test senden"}
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5 italic">
                Die Mail sollte anschließend in Ihrem Outlook &bdquo;Gesendete Elemente&ldquo; auftauchen.
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" onClick={handleDisconnect}
                      className="text-red-600 hover:text-red-700 border-red-200" data-testid="ms-disconnect">
                <X size={14} className="mr-1.5" /> Verknüpfung trennen
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-slate-600 space-y-2">
              <p>
                Nach der Verknüpfung sendet Domivus Ihre E-Mails über Ihren
                <strong> Outlook-Account</strong>. Vorteile:
              </p>
              <ul className="list-disc ml-5 space-y-1 text-xs text-slate-500">
                <li>Mails erscheinen automatisch in Outlook &bdquo;Gesendete Elemente&ldquo;</li>
                <li>Sie können sie dort öffnen, weiterleiten, archivieren wie gewohnt</li>
                <li>Antworten Ihrer Empfänger landen direkt in Ihrer Outlook-Inbox</li>
                <li>Zustellbestätigungen laufen wie bei jeder normalen Outlook-Mail</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Benötigte Berechtigungen: <code>Mail.Send</code>, <code>User.Read</code>, <code>offline_access</code>.
                Domivus liest keine Ihrer bestehenden Mails — nur senden.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="ms-connect">
              <PlugsConnected size={14} className="mr-2" />
              {loading ? "Weiterleitung…" : "Mit Microsoft anmelden"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
